// src/services/database/userStructure.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { ProfileInitializationService } from "../profile/initialization";
import { UniversalDirectoryManager } from "../storage/universalDirectoryManager";
import type { ContentType } from "../storage/universalDirectoryManager";
import { VCardInitializationService } from "../vcard/initialization";
import { SVGInitializationService } from "../svg/initialization";
import { QRCodeInitialization } from "../qrcode/initialization";
import { QRCodeService } from "../qrCodeService";
import { useDebugStore } from "../../store/debugStore";
import type { UserProfile } from "../../types/user";
import type { VCardCategory } from "../../types/vcard";

interface UserStructureResult {
  success: boolean;
  collections: string[];
  error?: string;
}

export class UserDatabaseStructure {
  // Map folder names to content types for UniversalDirectoryManager
  private static readonly folderToContentType: Record<string, ContentType> = {
    profile: "profile",
    vcards: "vcard",
    qrcodesMetadata: "qrcode",
    collections: "user",
    svg: "svg",
  } as const;

  // Validate that folderToContentType covers all userSubfolders
  private static validateFolderMapping() {
    const missingFolders =
      UniversalDirectoryManager.STRUCTURE.userSubfolders.filter(
        (folder) => !(folder in this.folderToContentType),
      );
    if (missingFolders.length > 0) {
      throw new Error(
        `Missing content type mappings for folders: ${missingFolders.join(", ")}`,
      );
    }
  }

  static {
    // Run validation when the class is loaded
    UserDatabaseStructure.validateFolderMapping();
  }

  static getProfilePath(userId: string): string {
    return UniversalDirectoryManager.getPath({
      userId,
      contentType: "profile",
    });
  }

  static getVCardsPath(userId: string): string {
    return UniversalDirectoryManager.getPath({
      userId,
      contentType: "vcard",
    });
  }

  static getVCardPath(
    userId: string,
    category: string,
    state: "active" | "repository",
  ): string {
    return UniversalDirectoryManager.getPath({
      userId,
      contentType: "vcard",
      category,
      state,
    });
  }

  static getQRCodesPath(userId: string): string {
    return UniversalDirectoryManager.getPath({
      userId,
      contentType: "qrcode",
    });
  }

  static getQRCodePath(userId: string, qrId: string): string {
    return UniversalDirectoryManager.getPath({
      userId,
      contentType: "qrcode",
      category: qrId,
    });
  }

  static getSvgPath(userId: string, category: string): string {
    return UniversalDirectoryManager.getPath({
      userId,
      contentType: "svg",
      category,
    });
  }

  private static async initializeUserProfile(
    userId: string,
    email?: string,
    name?: string,
  ) {
    // NOTE:
    // Firestore rules allow owner writes to users/{userId}/... subcollections
    // but not to the root users/{userId} document itself.
    // We intentionally avoid creating a base root user document here so signup
    // can initialize profile/vcards under allowed paths.

    // Initialize profile if email and name are provided
    if (email && name) {
      console.log(`Initializing profile for ${userId}`);
      const profileSuccess =
        await ProfileInitializationService.verifyAndInitializeProfile(
          userId,
          email,
          name,
        );
      if (!profileSuccess) {
        console.error(`Failed to initialize profile for ${userId}`);
        throw new Error("Profile initialization failed");
      }

      // Get the profile data to use for vCard initialization
      const profileRef = doc(db, this.getProfilePath(userId), "data");
      const profileDoc = await getDoc(profileRef);
      if (!profileDoc.exists()) {
        console.error(`Profile document not found for ${userId}`);
        throw new Error("Profile document not found");
      }

      // Initialize vCards with profile data
      console.log(`Initializing vCards for ${userId}`);
      const userProfile = profileDoc.data();
      await VCardInitializationService.initializeUserVCards(
        userId,
        userProfile as UserProfile,
      );
    }
  }

  private static async initializeUserDirectories(userId: string) {
    // 5. Create collection markers using UniversalDirectoryManager
    const collections = UniversalDirectoryManager.STRUCTURE.userSubfolders.map(
      (folder) => ({
        name: folder,
        path:
          folder === "collections"
            ? `${UniversalDirectoryManager.STRUCTURE.base}/${userId}/collections` // Special case for collections folder
            : UniversalDirectoryManager.getPath({
                userId,
                contentType: this.folderToContentType[folder],
                isMetadata: folder.endsWith("Metadata"),
              }),
        contentType: this.folderToContentType[folder],
      }),
    );

    for (const { name, path, contentType } of collections) {
      console.log(`Verifying collection: ${name} at path: ${path}`);
      await UniversalDirectoryManager.verifyDirectory(path, contentType);

      // For qrcode content type, create storage directories for each category
      if (contentType === "qrcode") {
        const categories =
          UniversalDirectoryManager.STRUCTURE.categoryFolders.qrcodes || [];
        for (const category of categories) {
          const categoryPath = UniversalDirectoryManager.getPath({
            userId,
            contentType: "qrcode",
            category,
          });
          await UniversalDirectoryManager.verifyDirectory(
            categoryPath,
            "qrcode",
          );
        }
      }
    }
  }

  private static getRepairableQrCategory(value: unknown): VCardCategory | null {
    if (typeof value !== "string" || value === "snowGear") {
      return null;
    }

    if (
      UniversalDirectoryManager.STRUCTURE.categoryFolders.qrcodesMetadata.includes(
        value,
      )
    ) {
      return value as VCardCategory;
    }

    return null;
  }

  private static getSnowGearSubcategory(
    category: VCardCategory,
  ): string | null {
    return category.startsWith("snowGear_")
      ? category.slice("snowGear_".length)
      : null;
  }

  private static async repairQrCodeMetadata(userId: string): Promise<void> {
    const vcardCollectionPath = UniversalDirectoryManager.getPath({
      userId,
      contentType: "vcard",
    });
    const metadataCollectionPath = UniversalDirectoryManager.getPath({
      userId,
      contentType: "qrcode",
      isMetadata: true,
    });

    const vcardSnapshot = await getDocs(collection(db, vcardCollectionPath));

    for (const vcardDoc of vcardSnapshot.docs) {
      const category = this.getRepairableQrCategory(vcardDoc.data().category);
      if (!category) {
        continue;
      }

      const qrData = await QRCodeService.createQRCode({
        userId,
        vcardId: vcardDoc.id,
        category,
      });
      const subcategory = this.getSnowGearSubcategory(category);
      const metadataRef = doc(db, metadataCollectionPath, category);
      const metadataSnap = await getDoc(metadataRef);
      const existingMetadata = metadataSnap.exists()
        ? metadataSnap.data()
        : null;
      const version = qrData.version || 1;
      const shouldSeedScanMetadata =
        !existingMetadata || existingMetadata.metadata === undefined;
      const shouldWriteMetadata =
        !existingMetadata ||
        existingMetadata.userId !== userId ||
        existingMetadata.category !== category ||
        existingMetadata.url !== qrData.url ||
        existingMetadata.version !== version ||
        existingMetadata.createdAt === undefined ||
        shouldSeedScanMetadata ||
        (subcategory ? existingMetadata.subcategory !== subcategory : false);

      if (!shouldWriteMetadata) {
        continue;
      }

      const metadataPayload: Record<string, unknown> = {
        userId,
        category,
        url: qrData.url,
        version,
        updatedAt: serverTimestamp(),
        ...(subcategory ? { subcategory } : {}),
      };

      if (!existingMetadata || existingMetadata.createdAt === undefined) {
        metadataPayload.createdAt = serverTimestamp();
      }

      if (shouldSeedScanMetadata) {
        metadataPayload.metadata = {
          printHistory: [],
          lastScanned: null,
          scanHistory: [],
        };
      }

      await setDoc(metadataRef, metadataPayload, { merge: true });
    }
  }

  private static async hasMissingSvg(userId: string): Promise<boolean> {
    const svgInit =
      await SVGInitializationService.validateInitialization(userId);
    return !svgInit.isInitialized;
  }

  private static async initializeMissingSvgs(userId: string): Promise<void> {
    const svgInitialized =
      await SVGInitializationService.initializeUserSVGDirectories(userId);
    if (!svgInitialized) {
      throw new Error("SVG initialization failed");
    }
  }

  private static async repairExistingUserStructure(
    userId: string,
    profileData: Record<string, unknown>,
  ): Promise<void> {
    const vcardInit =
      await VCardInitializationService.validateInitialization(userId);
    if (!vcardInit.isInitialized) {
      await VCardInitializationService.initializeUserVCards(
        userId,
        profileData as unknown as UserProfile,
      );
    }

    await this.repairQrCodeMetadata(userId);

    if (await this.hasMissingSvg(userId)) {
      await this.initializeMissingSvgs(userId);
    }
  }

  static async ensureUserStructure(
    userId: string,
    email?: string,
    name?: string,
  ): Promise<UserStructureResult> {
    const addDebugMessage = useDebugStore.getState().addMessage;

    try {
      // Fast-exit for already-initialized users: a single read replaces ~40+
      // sequential Firestore/Storage operations that would otherwise run on
      // every login. authStore.loadUserProfile still validates vCards and SVGs
      // separately, providing a safety net for any truly missing data.
      const profileRef = doc(db, this.getProfilePath(userId), "data");
      const profileDoc = await getDoc(profileRef);
      if (profileDoc.exists()) {
        await this.repairExistingUserStructure(userId, profileDoc.data());
        return {
          success: true,
          collections: UniversalDirectoryManager.STRUCTURE.userSubfolders,
        };
      }

      if (!email || !name) {
        // Auth-only accounts can exist if signup was interrupted before
        // profile creation. Let the caller create the profile with user details
        // instead of attempting QR/SVG setup with no vCards.
        return {
          success: true,
          collections: [],
        };
      }

      // New user — run full initialization chain.
      // Step 1: Profile + vCards and directories in parallel.
      // QR token init must come AFTER vCards exist, so it is NOT in this
      // Promise.all — it runs sequentially below.
      await Promise.all([
        this.initializeUserProfile(userId, email, name),
        this.initializeUserDirectories(userId),
      ]);

      // Step 2: Create QR tokens (requires vCards from step 1).
      // SVG init runs after so that createExampleSVG can use the real token URL.
      const qrMetadataInitialized =
        await QRCodeInitialization.initializeQRCodeMetadata(userId);
      if (!qrMetadataInitialized) {
        throw new Error("QR code metadata initialization failed");
      }

      // Step 3: Generate QR code SVGs using the token URLs from step 2.
      const svgInitialized =
        await SVGInitializationService.initializeUserSVGDirectories(userId);
      if (!svgInitialized) {
        throw new Error("SVG initialization failed");
      }

      addDebugMessage("success", "User structure initialized successfully");
      return {
        success: true,
        collections: UniversalDirectoryManager.STRUCTURE.userSubfolders,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      addDebugMessage(
        "error",
        `Failed to initialize user structure: ${errorMessage}`,
      );
      return {
        success: false,
        error: errorMessage,
        collections: [],
      };
    }
  }
}
